import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import t from 'react-translate';
import { config } from '@config/pendo.config.json';

import { useAppDispatch } from 'redux/store';
import { inPersonEventConfirmAttendance } from 'redux/actions/in-person-events';
import { RootState } from 'redux/schemas';
import { AttendeeInfoStatus } from 'redux/schemas/models/in-person-event';
import { ActivityProgress } from 'redux/schemas/models/activity';

import NvIcon from 'shared/components/nv-icon';
import { useInPersonEventContext } from '../in-person-event-context';

type WillAttendCtaProps = {
  released: boolean;
  progress: ActivityProgress
};

const WillAttendCta = ({ released, progress }: WillAttendCtaProps) => {
  const { catalogId, isAdmin, isEdit, sessionId } = useInPersonEventContext();
  const dispatch = useAppDispatch();

  const session = useSelector((state: RootState) => (
    sessionId ? state.models.inPersonEventSessions[sessionId] : null
  ));
  const confirmedRegistration = session?.confirmedRegistration ?? 0;
  const status = session?.status || progress;

  const [attendanceConfirming, setAttendanceConfirming] = useState(false);

  if (status === AttendeeInfoStatus.MISSED
    || status === AttendeeInfoStatus.CHECK_IN_QR_CODE
    || status === AttendeeInfoStatus.CHECK_IN_MANUAL
    || status === AttendeeInfoStatus.COMPLETED
  ) {
    return null;
  }

  const isAttendanceConfirmed = status === AttendeeInfoStatus.IS_CONFIRMED;

  const disableAttend = isEdit
    || !released
    || attendanceConfirming
    || isAttendanceConfirmed;

  const confirmAttendance = async () => {
    if (!sessionId) return;
    setAttendanceConfirming(true);
    try {
      await dispatch(inPersonEventConfirmAttendance({ catalogId, sessionId }));
    } finally {
      setAttendanceConfirming(false);
    }
  };

  return (
    <div className='d-flex justify-content-center align-items-center flex-column mt-6'>
      {isAttendanceConfirmed && !isAdmin && (
        <div className='label text-success mb-2'>
          {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.ATTENDANCE_CONFIRMED()}
        </div>
      )}
      <Button
        variant='primary'
        disabled={disableAttend}
        onClick={confirmAttendance}
        data-qa={config.pendo.inPersonEvent.willAttendButton}
      >
        {attendanceConfirming
          ? t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CONFIRMING()
          : (
            <div className='d-flex align-items-center'>
              {isAttendanceConfirmed && <NvIcon icon='check' size='xss-smallest' className='mr-2' />}
              {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.WILL_ATTEND()}
            </div>
          )}
      </Button>
      {isAttendanceConfirmed && !isAdmin && (
        <div className='label text-black mt-2'>
          {t.LECTURE_PAGES.COMPONENTS.IN_PERSON_EVENTS.CONFIRMATIONS(confirmedRegistration)}
        </div>
      )}
    </div>
  );
};

export default WillAttendCta;
